import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Quantum } from "ldrs/react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../assets/css/Cart.css";
import { NotificationManager } from "react-notifications";

const PaymentLink = () => {
  const { key } = useParams();
  const [paymentLink, setPaymentLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(""); // Payment status message

  useEffect(() => {
    const fetchPaymentLink = async () => {
      if (!key) {
        NotificationManager.warning("Payment link key missing");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_HOST_API_URL}/payment/payment-link-data/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ payment_link: key }),
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.detail || "Failed to fetch payment link");
        }

        setPaymentLink(result.payment_link_data);

        // Set the payment status if available
        if (result.detail) {
          setPaymentStatus(result.detail);
        }
      } catch (err) {
        NotificationManager.error(err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentLink();
  }, [key]);

  // 💳 Calculate convenience fee
  const calculatePayNowFee = (totalWithSurcharge) => {
    let fee = 0.0;
    if (totalWithSurcharge >= 100) {
      fee = totalWithSurcharge * 0.0065 + 0.3;
    } else {
      fee = totalWithSurcharge * 0.009;
      if (fee < 0.2) fee = 0.2;
    }
    return parseFloat(fee.toFixed(2));
  };

  const handleProceedToPay = async () => {
    if (!paymentLink) return;
    setInitiating(true);

    try {
      const baseAmount = paymentLink.amount;
      const fee = calculatePayNowFee(baseAmount);
      const totalAmount = baseAmount + fee;

      const response = await fetch(
        `${process.env.REACT_APP_HOST_API_URL}/payment/initiate-payment-link/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            payment_link: key,
            amount: totalAmount,
            tax: fee,
          }),
        }
      );

      console.log("Payment initiation raw response:", response);

      const result = await response.json();
      console.log("Initiate Payment Response:", result);

      if (!response.ok) {
        throw new Error(result.detail || "Failed to initiate payment");
      }

      if (result.checkout_url) {
        console.log("Redirecting to HitPay checkout...");
        window.location.href = result.checkout_url;
      } else {
        console.error("checkout_url missing in response", result);
      }
    } catch (err) {
      console.error("Payment initiation failed:", err.message);
    } finally {
      setInitiating(false);
    }
  };

  return (
    <div>
      {/* Navbar */}
      <div className="child-nav bg-white">
        <Navbar />
      </div>

      {/* Main Container */}
      <div className="container mt-4">
        <h2 className="mb-4" style={{ color: "#0564bf" }}>
          Payment Details
        </h2>

        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "300px" }}
          >
            <Quantum size="45" speed="1.75" color="blue" />
          </div>
        ) : error ? (
          <div className="text-center text-danger mt-4">{error}</div>
        ) : paymentLink ? (
          <div className="row">
            {/* Left side - Payer Info */}
            <div className="col-12 col-lg-8">
              <div className="cart-item px-4 py-4 mb-4">
                <h5 className="card-title">Payer Details</h5>
                <hr />
                <p>
                  <strong>Name:</strong> {paymentLink.name}
                </p>
                <p>
                  <strong>Email:</strong> {paymentLink.email}
                </p>
                <p>
                  <strong>Mobile:</strong> {paymentLink.mobile}
                </p>
              </div>
            </div>

            {/* Right side - Payment Summary */}
            <div className="col-12 col-lg-4 mt-4 mt-lg-0">
              <div className="px-4 py-4 cart-item">
                <h5 className="card-title">Payment Summary</h5>
                <hr />

                {/* Payment Status */}
                {paymentStatus && (
                  <div
                    className={`alert ${
                      paymentStatus.includes("failed")
                        ? "alert-danger"
                        : paymentStatus.includes("completed")
                        ? "alert-success"
                        : "alert-warning"
                    }`}
                  >
                    {paymentStatus}
                  </div>
                )}

                {(() => {
                  const baseAmount = paymentLink.amount;
                  const fee = calculatePayNowFee(baseAmount);
                  const total = baseAmount + fee;

                  // Hide Terms & Button if payment is completed, failed, or pending
                  const showPaymentOptions = !(
                    paymentStatus.includes("completed") ||
                    paymentStatus.includes("failed") ||
                    paymentStatus.includes("pending")
                  );

                  return (
                    <>
                      <p className="d-flex justify-content-between">
                        <span>Subtotal:</span>
                        <strong>${baseAmount.toFixed(2)}</strong>
                      </p>

                      <p className="d-flex justify-content-between">
                        <span>Convenience Fee:</span>
                        <strong>${fee.toFixed(2)}</strong>
                      </p>

                      <hr />

                      <p className="d-flex justify-content-between">
                        <span>Total:</span>
                        <strong>${total.toFixed(2)}</strong>
                      </p>

                      {showPaymentOptions && (
                        <>
                          {/* Terms & Conditions */}
                          <div className="form-check my-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="termsCheck"
                              checked={agreed}
                              onChange={(e) => setAgreed(e.target.checked)}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="termsCheck"
                            >
                              I agree with{" "}
                              <Link
                                to="/conditions"
                                className="text-primary text-decoration-none"
                              >
                                Terms & Conditions
                              </Link>
                            </label>
                          </div>

                          {/* Proceed Button */}
                          <button
                            className="btn btn-outline-primary w-100"
                            disabled={!agreed || initiating}
                            onClick={handleProceedToPay}
                          >
                            {initiating ? "Processing..." : "Proceed to Pay"}
                          </button>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted mt-4">
            No payment link data found
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PaymentLink;
